import React, { useMemo } from 'react'
import { Modal } from 'react-bootstrap'
import { formatExperienceDate } from '../../utils/date'

const EXCLUDED_QUESTIONS = new Set(['full name', 'email'])

const cleanFactLabel = (question = '') =>
  String(question)
    .replace(/\s*\((optional|if applicable)\)\s*$/i, '')
    .trim()

const getAnswerText = (answer) => {
  if (Array.isArray(answer?.answer)) {
    return String(answer.answer[0] ?? '').trim()
  }
  if (answer?.answer == null) return ''
  return String(answer.answer).trim()
}

const formatOccupancyDisplay = (preference, investments) => {
  if (!preference) return ''
  const pref = String(preference).trim().toLowerCase()
  const label = pref.charAt(0).toUpperCase() + pref.slice(1)
  if (!investments) return label

  const standardRate = Number(investments.standardRate) || 0
  const singleRate = Number(investments.singleSupplement) || 0
  const amount = pref === 'single' ? singleRate : standardRate

  return `${label} · $${amount.toLocaleString('en-US')}`
}

const InquiriesModal = ({
  showModal,
  handleClose,
  inquirieDetail,
  investments,
  onEnroll,
}) => {
  const canEnroll =
    typeof onEnroll === 'function' &&
    inquirieDetail &&
    !inquirieDetail.isEnrolled

  const { facts, writtenAnswers } = useMemo(() => {
    const answers = Array.isArray(inquirieDetail?.answers)
      ? inquirieDetail.answers
      : []
    const nextFacts = []
    const nextWritten = []

    answers.forEach((answer) => {
      const questionKey = String(answer?.question || '')
        .trim()
        .toLowerCase()
      if (EXCLUDED_QUESTIONS.has(questionKey)) return

      const hasHeading = Boolean(answer?.description?.trim())
      const isLongForm =
        answer?.answerType === 'textarea' ||
        answer?.answerType === 'long_text' ||
        hasHeading

      if (isLongForm) {
        nextWritten.push(answer)
      } else {
        nextFacts.push(answer)
      }
    })

    return { facts: nextFacts, writtenAnswers: nextWritten }
  }, [inquirieDetail])

  const occupancyLabel = formatOccupancyDisplay(
    inquirieDetail?.occupancyPreference,
    investments,
  )
  const submittedAt = formatExperienceDate(inquirieDetail?.createdAt)
  const leadParts = [
    inquirieDetail?.email,
    submittedAt ? `submitted ${submittedAt}` : null,
  ].filter(Boolean)

  const renderValue = (answer) => {
    const value = getAnswerText(answer)
    if (!value) {
      return (
        <span className='experience-inquiry-empty'>No answer provided</span>
      )
    }
    if (answer?.answerType === 'url') {
      return (
        <a href={value} target='_blank' rel='noopener noreferrer'>
          {value}
        </a>
      )
    }
    return value
  }

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      centered
      enforceFocus={false}
      dialogClassName='experience-inquiry-dialog'
      contentClassName='experience-response-card'>
      <Modal.Body className='p-0'>
        <div className='experience-response-modal experience-inquiry-modal'>
          <div className='experience-response-head'>
            <h2>
              Inquiry —{' '}
              <span className='text-capitalize'>
                {inquirieDetail?.fullName || ''}
              </span>
            </h2>
            <span
              className={`badge-experience ${
                inquirieDetail?.isEnrolled ? 'badge-live' : 'badge-soon'
              }`}>
              {inquirieDetail?.isEnrolled ? 'Enrolled' : 'New'}
            </span>
          </div>
          {leadParts.length > 0 ? (
            <p className='experience-response-lead'>{leadParts.join(' · ')}</p>
          ) : null}

          <div className='experience-response-qa experience-inquiry-qa'>
            {(facts.length > 0 || occupancyLabel) && (
              <div className='experience-inquiry-facts'>
                {facts.map((answer, index) => (
                  <div
                    className='experience-status-line'
                    key={answer?.questionId || answer?.question || index}>
                    <span>{cleanFactLabel(answer?.question)}</span>
                    <b>{renderValue(answer)}</b>
                  </div>
                ))}
                {occupancyLabel ? (
                  <div className='experience-status-line'>
                    <span>Occupancy</span>
                    <b>{occupancyLabel}</b>
                  </div>
                ) : null}
              </div>
            )}

            {writtenAnswers.length > 0 ? (
              <>
                <div className='experience-field-head'>
                  <h4>Written answers</h4>
                </div>
                {writtenAnswers.map((answer, index) => (
                  <div
                    className='experience-inquiry-sec'
                    key={answer?.questionId || answer?.question || index}>
                    {answer?.description ? (
                      <div className='experience-inquiry-sec-head'>
                        <h3>{answer.description}</h3>
                      </div>
                    ) : null}
                    <div className='experience-inquiry-sec-body'>
                      {answer?.question ? (
                        <p className='experience-inquiry-prompt'>
                          {answer.question}
                        </p>
                      ) : null}
                      <p className='experience-inquiry-answer'>
                        {renderValue(answer)}
                      </p>
                    </div>
                  </div>
                ))}
              </>
            ) : null}

            {facts.length === 0 &&
            writtenAnswers.length === 0 &&
            !occupancyLabel ? (
              <p className='experience-inquiry-empty'>
                No inquiry answers available.
              </p>
            ) : null}
          </div>

          <div className='experience-modal-actions'>
            <button
              type='button'
              className='btn-outline-experience'
              onClick={handleClose}>
              Close
            </button>
            {canEnroll ? (
              <button
                type='button'
                className='btn-navy'
                onClick={() => {
                  handleClose()
                  onEnroll(inquirieDetail)
                }}>
                Enroll
              </button>
            ) : null}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default InquiriesModal
